export {}

declare const window: any;

type FacebookSdk = {
    init: (options: {
        appId: string;
        xfbml: boolean;
        version: string;
    }) => void;
    ui: (
        options: {
            method: 'share';
            href: string;
            mobile_iframe?: boolean;
        },
        callback?: (response: unknown) => void,
    ) => void;
};

const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID;
const FACEBOOK_API_VERSION = 'v21.0';
const FACEBOOK_SDK_SCRIPT_ID = 'facebook-jssdk';
const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

let facebookSdkPromise: Promise<FacebookSdk | null> | null = null;
let isFacebookSdkInitialized = false;

export function clarityEvent(eventName : string){
    if(window && window.clarity){
        window.clarity("event", eventName)
    }
}

function getFacebookSdk(): FacebookSdk | null {
    const facebookSdk = window.FB as FacebookSdk | undefined;

    if (!facebookSdk) {
        return null;
    }

    if (!isFacebookSdkInitialized) {
        facebookSdk.init({
            appId: FACEBOOK_APP_ID,
            xfbml: false,
            version: FACEBOOK_API_VERSION,
        });
        isFacebookSdkInitialized = true;
    }

    return facebookSdk;
}

function loadFacebookSdk(): Promise<FacebookSdk | null> {
    if (typeof document === 'undefined') {
        return Promise.resolve(null);
    }

    const facebookSdk = getFacebookSdk();
    if (facebookSdk) {
        return Promise.resolve(facebookSdk);
    }

    if (facebookSdkPromise) {
        return facebookSdkPromise;
    }

    facebookSdkPromise = new Promise<FacebookSdk | null>((resolve, reject) => {
        const finishLoading = () => {
            const loadedSdk = getFacebookSdk();

            if (loadedSdk) {
                resolve(loadedSdk);
                return;
            }

            reject(new Error('Facebook SDK did not initialize.'));
        };

        const previousAsyncInit = window.fbAsyncInit;
        window.fbAsyncInit = () => {
            previousAsyncInit?.();
            finishLoading();
        };

        const existingScript = document.getElementById(FACEBOOK_SDK_SCRIPT_ID) as HTMLScriptElement | null;
        if (existingScript) {
            existingScript.addEventListener('load', finishLoading, { once: true });
            existingScript.addEventListener(
                'error',
                () => reject(new Error('Failed to load Facebook SDK.')),
                { once: true },
            );
            return;
        }

        const script = document.createElement('script');
        script.id = FACEBOOK_SDK_SCRIPT_ID;
        script.async = true;
        script.defer = true;
        script.src = FACEBOOK_SDK_URL;
        script.addEventListener('load', finishLoading, { once: true });
        script.addEventListener(
            'error',
            () => reject(new Error('Failed to load Facebook SDK.')),
            { once: true },
        );
        document.body.appendChild(script);
    }).catch(() => {
        facebookSdkPromise = null;
        return null;
    });

    return facebookSdkPromise;
}


export function isNPSTestCompany (companyUID:string){
    const dont_redirect_these = ['quantum-nexus-labs', 'mike-test-company'];
    return dont_redirect_these.includes(companyUID);
}

export function preloadFacebookSdk() {
    void loadFacebookSdk();
}

export async function shareToFacebook(url: string) {
    const facebookSdk = getFacebookSdk() ?? await loadFacebookSdk();

    if (facebookSdk) {
        facebookSdk.ui({
            method: 'share',
            href: url,
        });
        return;
    }

    const encodedUrl = encodeURIComponent(url);

    safeOpen(
        `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        'height=500,width=530,menubar=no,status=no,toolbar=no',
    );
}

export function safeOpen(url: string, features?: string) {
  const w = window.open(url, '_blank', features);
  if (!w || w.closed || typeof w.closed === 'undefined') location.href = url;
};

